import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { PUBLIC } from "@/constants/testIds";
import { useSiteTheme } from "@/components/public/useSiteTheme";
import SeoHead from "@/components/public/SeoHead";
import TurnstileWidget from "@/components/public/TurnstileWidget";

export default function PublicContact() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const initialKind = params.get("kind") === "advertise" ? "advertise" : "contact";
  const [site, setSite] = useState(null);
  const [kind, setKind] = useState(initialKind);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");

  useEffect(() => { publicApi.get(`/public/sites/${slug}/meta`).then(r => setSite(r.data)); }, [slug]);
  useSiteTheme(site);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    try {
      await publicApi.post("/public/forms/contact", {
        site_slug: slug, kind, name, email, company, message,
        turnstile_token: turnstileToken || undefined,
      });
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to send.");
    }
  }

  if (!site) return <div className="public-site min-h-screen flex items-center justify-center"><div className="opacity-60 mono">Loading…</div></div>;

  return (
    <div className="public-site">
      <SeoHead
        site={site}
        title={kind === "advertise" ? "Advertise" : "Contact"}
        description={kind === "advertise" ? "Reach our readers." : "Tips, corrections, partnerships, or just hello."}
      />
      <nav className="border-b" style={{ borderColor: "var(--site-rule)" }}>
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-6 py-3 text-sm">
          <Link to={`/site/${site.slug}`} className="display text-lg font-bold">{site.name}</Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="display text-4xl font-black tracking-tight">
          {kind === "advertise" ? "Advertise with us." : "Get in touch."}
        </h1>
        <p className="mt-2 opacity-80">
          {kind === "advertise"
            ? "Reach our readers. Tell us about your campaign and we'll respond within 1 business day."
            : "Tips, corrections, partnerships, or just hello — we read every message."}
        </p>

        <div className="mt-6 flex gap-2 text-xs">
          <button onClick={() => setKind("contact")} className={`px-3 py-1.5 border ${kind === "contact" ? "" : "opacity-50"}`} style={{ borderColor: "var(--site-rule)" }}>General inquiry</button>
          <button onClick={() => setKind("advertise")} className={`px-3 py-1.5 border ${kind === "advertise" ? "" : "opacity-50"}`} style={{ borderColor: "var(--site-rule)" }}>Advertise</button>
        </div>

        {done ? (
          <div className="mt-8 border p-6" style={{ borderColor: "var(--site-rule)" }}>
            <div className="display text-xl font-bold">Thanks — message received.</div>
            <div className="text-sm opacity-80 mt-2">We&apos;ll reply to {email} as soon as possible.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label="Your name">
              <input data-testid={PUBLIC.contactName} required value={name} onChange={(e) => setName(e.target.value)} className="form-input" />
            </Field>
            <Field label="Email">
              <input data-testid={PUBLIC.contactEmail} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="form-input" />
            </Field>
            {kind === "advertise" && (
              <Field label="Company">
                <input value={company} onChange={(e) => setCompany(e.target.value)} className="form-input" />
              </Field>
            )}
            <Field label="Message">
              <textarea data-testid={PUBLIC.contactMessage} required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} className="form-input" />
            </Field>
            {err && <div className="text-sm text-red-600">{err}</div>}
            <TurnstileWidget onToken={setTurnstileToken} />
            <button
              data-testid={PUBLIC.contactSubmit}
              className="px-6 py-2 text-sm font-semibold"
              style={{ background: "var(--site-accent)", color: "#fff" }}
            >
              {kind === "advertise" ? "Send Inquiry" : "Send Message"}
            </button>
          </form>
        )}
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: transparent;
          border: 1px solid var(--site-rule);
          padding: 0.5rem 0.75rem;
          color: var(--site-text);
          font-size: 0.95rem;
          outline: none;
        }
        .form-input:focus {
          box-shadow: 0 0 0 1px var(--site-accent);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest opacity-60 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
